<template>
	<section id="categories" class="sg-section">
		<v-container>
			<h2 class="sg-section-title">دسته‌بندی زیورآلات</h2>
			<div class="sg-gold-line"></div>
			<p class="sg-section-subtitle">دسته مورد نظر خود را انتخاب کنید و محصولات را مشاهده کنید</p>

			<v-row justify="center" class="mb-8">
				<v-col v-for="cat in categories" :key="cat.id" cols="6" sm="4" md="2">
					<div class="sg-category-chip" :class="{ active: selectedCategory === cat.id }" @click="selectCategory(cat.id)">
						<div class="sg-cat-icon">
							<v-icon color="#C9A227" size="32">{{ cat.icon }}</v-icon>
						</div>
						<div class="font-weight-bold">{{ cat.name }}</div>
					</div>
				</v-col>
			</v-row>

			<transition-group name="sg-list" tag="div">
				<v-row :key="selectedCategory">
					<v-col v-for="product in filteredProducts" :key="product.id" cols="12" sm="6" md="4" lg="3">
						<v-card class="sg-product-card" elevation="0">
							<div class="sg-product-img" @click="openQuickView(product)">
								<img :src="product.gallery[0]" :alt="product.name" style="width:100%;height:100%;object-fit:cover;" />
								<div class="sg-quick-view-overlay">
									<v-icon color="#1a1a1a">mdi-magnify-plus</v-icon>
									<span>مشاهده سریع</span>
								</div>
							</div>
							<v-card-text class="pa-4">
								<h3 class="font-weight-bold mb-2 text-center pointer" style="font-size: 1rem;" @click="openQuickView(product)">{{ product.name }}</h3>
								<div class="d-flex flex-wrap mb-3" style="gap: 8px;">
									<span class="sg-price-tag">
										<v-icon x-small color="#C9A227">mdi-scale-balance</v-icon>
										{{ product.weight }} گرم
									</span>
									<span class="sg-price-tag">
										<v-icon x-small color="#C9A227">mdi-hammer-wrench</v-icon>
										اجرت {{ product.wage }}٪
									</span>
								</div>
								<div class="d-flex align-center justify-space-between">
									<div>
										<div class="grey--text text--darken-1" style="font-size: 0.75rem;">قیمت</div>
										<div class="font-weight-bold" style="color: #C9A227; font-size: 1.1rem;">
											{{ formatPrice(product.price) }}
											<small class="grey--text">تومان</small>
										</div>
									</div>
									<div class="d-flex align-center" style="gap: 4px;">
										<v-btn class="sg-btn-gold" small @click="$emit('order', product)">
											<v-icon left small>mdi-cart-plus</v-icon>
											ثبت سفارش
										</v-btn>
									</div>
								</div>
							</v-card-text>
						</v-card>
					</v-col>
				</v-row>
			</transition-group>

			<div v-if="!filteredProducts.length" class="text-center py-8 grey--text">
				<v-icon size="48" color="grey lighten-1">mdi-package-variant</v-icon>
				<p class="mt-3">محصولی در این دسته یافت نشد</p>
			</div>

			<v-dialog v-model="quickViewDialog" max-width="720px" scrollable>
				<v-card class="sg-quick-view-card" v-if="selectedProduct">
					<div class="sg-quick-view-layout">
						<div class="sg-quick-view-gallery">
							<v-carousel v-if="selectedProduct.gallery && selectedProduct.gallery.length" height="360" hide-delimiter-background>
								<v-carousel-item v-for="(img, i) in selectedProduct.gallery" :key="i">
									<img :src="img" :alt="selectedProduct.name + ' ' + (i + 1)" class="sg-gallery-img" />
								</v-carousel-item>
							</v-carousel>
							<div v-else class="sg-gallery-fallback">
								<v-icon size="96" color="#C9A227">{{ selectedProduct.icon }}</v-icon>
							</div>
							<div class="sg-gallery-badge">
								<v-icon small>mdi-camera-outline</v-icon>
								<span>{{ selectedProduct.gallery ? selectedProduct.gallery.length : 0 }} عکس</span>
							</div>
						</div>

						<div class="sg-quick-view-info pa-6">
							<div class="d-flex align-center justify-space-between mb-2">
								<v-chip small color="#C9A227" dark>
									<v-icon x-small left>mdi-tag-outline</v-icon>
									{{ getCategoryName(selectedProduct.category) }}
								</v-chip>
								<v-btn icon small @click="quickViewDialog = false">
									<v-icon>mdi-close</v-icon>
								</v-btn>
							</div>

							<h2 class="font-weight-bold mb-3" style="font-size: 1.4rem;">{{ selectedProduct.name }}</h2>

							<div class="d-flex flex-wrap justify-space-between mb-4" style="gap: 8px;">
								<span class="sg-price-tag">
									<v-icon x-small color="#C9A227">mdi-scale-balance</v-icon>
									وزن: {{ selectedProduct.weight }} گرم
								</span>
								<span class="sg-price-tag">
									<v-icon x-small color="#C9A227">mdi-hammer-wrench</v-icon>
									اجرت: {{ selectedProduct.wage }}٪
								</span>
								<span class="sg-price-tag">
									<v-icon x-small color="#C9A227">mdi-gold</v-icon>
									طلای ۱۸ عیار
								</span>
							</div>

							<v-divider class="mb-4"></v-divider>

							<div v-if="selectedProduct.description" class="mb-5">
								<h4 class="font-weight-bold mb-2" style="color: #C9A227; font-size: 0.95rem;">
									<v-icon x-small class="ml-1">mdi-text-box-outline</v-icon>
									توضیحات محصول
								</h4>
								<p class="grey--text text--darken-1 text-body-2" style="line-height: 2;">{{ selectedProduct.description }}</p>
							</div>

							<div class="d-flex align-end justify-space-between flex-wrap" style="gap: 12px;">
								<div>
									<div class="grey--text text--darken-1 subtitle-2 mb-1">قیمت روز</div>
									<div class="font-weight-bold" style="color: #C9A227; font-size: 1.6rem;">
										{{ formatPrice(selectedProduct.price) }}
										<small class="grey--text" style="font-size: 0.8rem;">تومان</small>
									</div>
								</div>
								<v-btn class="sg-btn-gold" large @click="quickOrder(selectedProduct)">
									<v-icon left>mdi-cart-plus</v-icon>
									ثبت سفارش
								</v-btn>
							</div>
						</div>
					</div>
				</v-card>
			</v-dialog>
		</v-container>
	</section>
</template>

<script>
export default {
	name: 'CategoriesSection',
	props: {
		goldPrice: { type: Number },
	},
	data() {
		return {
			selectedCategory: 'necklace',
			quickViewDialog: false,
			selectedProduct: null,
			categories: [
				{ id: 'necklace', name: 'گردنبند', icon: 'mdi-necklace' },
				{ id: 'ring', name: 'انگشتر', icon: 'mdi-ring' },
				{ id: 'set', name: 'ست طلا', icon: 'mdi-diamond-stone' },
				{ id: 'bracelet', name: 'دستبند', icon: 'mdi-watch-variant' },
				{ id: 'earring', name: 'گوشواره', icon: 'mdi-ear-hearing' },
				{ id: 'anklet', name: 'پابند', icon: 'mdi-foot-print' },
			],
			products: [
				{ id: 1, category: 'necklace', name: 'گردنبند طلا طرح گل', weight: 0.76, wage: 18, price: 38500000, icon: 'mdi-necklace', description: 'گردنبند طلای ۱۸ عیار با طرح گل رز، مناسب استفاده روزمره و مجلسی. این محصول با دقت بالا و طراحی ظریف ساخته شده و دارای زنجیر ظریف طلایی است.', gallery: ['https://safegolds.com/gallery/IMG_20260914_190645_248.jpg', 'https://safegolds.com/gallery/IMG_20260913_105525_942.jpg', 'https://safegolds.com/gallery/IMG_20260913_105525_942.jpg'] },
				{ id: 2, category: 'necklace', name: 'گردنبند طلا زنجیر ظریف', weight: 3.8, wage: 15, price: 34200000, icon: 'mdi-necklace', description: 'گردنبند زنجیری ظریف از جنس طلای ۱۸ عیار، سبک و راحت برای استفاده روزانه. طراحی مینیمال و شیک که با هر استایلی هماهنگ می‌شود.', gallery: ['https://safegolds.com/gallery/IMG_20260913_105525_942.jpg', 'https://safegolds.com/gallery/IMG_20260913_105522_901.jpg'] },
				{ id: 3, category: 'ring', name: 'انگشتر طلا نگین دار', weight: 4.74, wage: 20, price: 19800000, icon: 'mdi-ring', description: 'انگشتر طلای ۱۸ عیار با نگین زیرکونیا درخشان. طراحی خاص و مناسب برای هدیه دادن در مناسبت‌های ویژه.', gallery: ['https://safegolds.com/gallery/IMG_20260915_172418_988.jpg', 'https://safegolds.com/gallery/IMG_20260915_172413_715.jpg', 'https://safegolds.com/gallery/IMG_20260505_102618_253.jpg'] },
				{ id: 4, category: 'ring', name: 'انگشتر طلا ساده', weight: 5.68, wage: 14, price: 16500000, icon: 'mdi-ring', description: 'انگشتر ساده و شیک از طلای ۱۸ عیار، مناسب استفاده روزمره. طراحی کلاسیک و بدون نگین که هیچ‌گاه از مد نمی‌افتد.', gallery: ['https://safegolds.com/gallery/IMG_20260506_120114_862.jpg', 'https://safegolds.com/gallery/IMG_20260506_120114_862.jpg'] },
				{ id: 5, category: 'set', name: 'ست طلا گردنبند و گوشواره', weight: 2.85, wage: 17, price: 78200000, icon: 'mdi-diamond-stone', description: 'ست کامل گردنبند و گوشواره طلای ۱۸ عیار با طرح هماهنگ. مناسب برای هدیه عروسی و نامزدی، این ست با جعبه مخملی ارائه می‌شود.', gallery: ['https://safegolds.com/gallery/IMG_20260915_172406_119.jpg'] },
				{ id: 6, category: 'set', name: 'ست طلا مجلسی', weight: 12.3, wage: 22, price: 115000000, icon: 'mdi-diamond-stone', description: 'ست مجلسی شامل گردنبند، گوشواره و دستبند طلای ۱۸ عیار. طراحی لوکس و خیره‌کننده مناسب مراسم خاص و مهمانی‌های مجلل.', gallery: ['https://safegolds.com/gallery/IMG_20260915_110222_923.jpg', 'https://safegolds.com/gallery/IMG_20260915_110227_167.jpg', 'https://safegolds.com/gallery/IMG_20260915_110218_341.jpg'] },
				{ id: 7, category: 'bracelet', name: 'دستبند طلا بافت', weight: 0.8, wage: 16, price: 51200000, icon: 'mdi-watch-variant', description: 'دستبند طلای بافته شده با ظرافت بالا. طراحی منحصربه‌فرد و دست‌ساز که حس لوکس بودن را به شما هدیه می‌دهد.', gallery: ['https://safegolds.com/gallery/IMG_20260820_120945_288.jpg'] },
				{ id: 8, category: 'bracelet', name: 'دستبند طلا زنجیری', weight: 3.5, wage: 15, price: 44800000, icon: 'mdi-watch-variant', description: 'دستبند زنجیری ظریف از طلای ۱۸ عیار، سبک و راحت. مناسب ست کردن با ساعت و سایر زیورآلات.', gallery: ['https://safegolds.com/gallery/IMG_20260818_111948_365.jpg'] },
				{ id: 9, category: 'earring', name: 'گوشواره طلا میخی', weight: 2.710, wage: 19, price: 14200000, icon: 'mdi-ear-hearing', description: 'گوشواره میخی ساده و شیک از طلای ۱۸ عیار. مناسب استفاده روزمره و راحت برای پوشیدن طولانی‌مدت.', gallery: ['https://safegolds.com/gallery/IMG_20260901_120555_328.jpg'] },
				{ id: 10, category: 'earring', name: 'گوشواره طلا آویز', weight: 4.01, wage: 21, price: 26500000, icon: 'mdi-ear-hearing', description: 'گوشواره آویز طلای ۱۸ عیار با طراحی خاص و زیبا. حرکت آزادانه آن هنگام راه رفتن جلوه‌ای زیبا به چهره شما می‌بخشد.', gallery: ['https://safegolds.com/gallery/IMG_20260819_121225_674.jpg'] },
				{ id: 11, category: 'anklet', name: 'پابند طلا ظریف', weight: 1.2, wage: 16, price: 29800000, icon: 'mdi-foot-print', description: 'پابند ظریف طلای ۱۸ عیار مناسب فصول گرم. طراحی سبک و راحت که در تابستان جلوه زیبایی به پاهای شما می‌دهد.', gallery: ['https://safegolds.com/gallery/Annotation-2026-09-16-125426.jpg'] },
				{ id: 12, category: 'anklet', name: 'پابند طلا زنجیر', weight: 2.9, wage: 14, price: 26800000, icon: 'mdi-foot-print', description: 'پابند زنجیری از جنس طلای ۱۸ عیار با قفل محکم. مناسب استفاده روزمره و مهمانی‌ها.', gallery: ['https://safegolds.com/gallery/Annotation-2026-09-16-125359.jpg'] },
			],
		}
	},
	computed: {
		filteredProducts() {
			return this.products.map(x => {
				let prd = { ...x }
				prd.price = prd.weight * this.goldPrice
				return prd
			}).filter(p => p.category === this.selectedCategory)
		}
	},
	methods: {
		selectCategory(id) {
			this.selectedCategory = id
		},
		formatPrice(val) {
			return Number(val).toLocaleString('fa-IR')
		},
		getCategoryName(catId) {
			const cat = this.categories.find(c => c.id === catId)
			return cat ? cat.name : ''
		},
		openQuickView(product) {
			this.selectedProduct = product
			this.quickViewDialog = true
		},
		quickOrder(product) {
			this.quickViewDialog = false
			this.$emit('order', product)
		},
	},
}
</script>

<style scoped>
.sg-list-enter-active,
.sg-list-leave-active {
	transition: all 0.4s ease;
}

.sg-list-enter,
.sg-list-leave-to {
	opacity: 0;
	transform: translateY(16px);
}

.sg-quick-view-overlay {
	position: absolute;
	inset: 0;
	background: rgba(201, 162, 39, 0.55);
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 8px;
	color: #1a1a1a;
	font-weight: 700;
	font-size: 0.9rem;
	opacity: 0;
	transition: opacity 0.35s ease;
	cursor: pointer;
}

.sg-product-card:hover .sg-quick-view-overlay {
	opacity: 1;
}

.sg-quick-view-card {
	border-radius: 16px !important;
	overflow: hidden;
}

.sg-quick-view-layout {
	display: flex;
	flex-wrap: wrap;
}

.sg-quick-view-gallery {
	position: relative;
	flex: 1 1 340px;
	max-width: 100%;
	background: linear-gradient(145deg, #FFF9F0, #F5E6A3);
}

.sg-quick-view-info {
	flex: 1 1 320px;
	min-width: 280px;
	display: flex;
	flex-direction: column;
}

.sg-gallery-img {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.sg-gallery-fallback {
	height: 360px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.sg-gallery-badge {
	position: absolute;
	top: 16px;
	right: 16px;
	background: rgba(26, 26, 26, 0.75);
	color: #fff;
	border-radius: 999px;
	padding: 4px 14px;
	font-size: 0.8rem;
	display: inline-flex;
	align-items: center;
	gap: 6px;
}

@media (max-width: 600px) {
	.sg-quick-view-gallery .v-carousel {
		height: 260px !important;
	}

	.sg-gallery-fallback {
		height: 260px;
	}
}
</style>
